/**
 * StatusToggleCell — inline toggle for Item active/disabled status.
 *
 * Inverts the `disabled` field so green = active (disabled=0)
 * and grey = disabled (disabled=1). Uses frappe.client.set_value
 * with optimistic update.
 *
 * QF-879: Customer reported "ปิดคือใช้งาน เขียวคือซ่อน" — the raw
 * disabled field renders backwards when shown as a toggle.
 */

import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { toast } from "sonner";

type StatusToggleCellProps = {
  itemName: string;
  isActive: boolean;
  language?: string;
};

export function StatusToggleCell({ itemName, isActive, language }: StatusToggleCellProps) {
  const [localActive, setLocalActive] = useState(isActive);
  const queryClient = useQueryClient();

  useEffect(() => {
    setLocalActive(isActive);
  }, [isActive]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (active: boolean) => {
      return frappeMethod<Record<string, unknown>>("frappe.client.set_value", {
        doctype: "Item",
        name: itemName,
        fieldname: { disabled: active ? 0 : 1 },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", "Item"] });
    },
  });

  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const prev = localActive;
    const next = !localActive;
    setLocalActive(next);

    try {
      await mutateAsync(next);
    } catch {
      setLocalActive(prev);
      toast.error(
        language === "th"
          ? "ไม่สามารถอัปเดตสถานะได้"
          : "Failed to update status",
      );
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-[36px] min-w-[44px]"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
    >
      <Switch
        checked={localActive}
        onCheckedChange={() => {}}
        onClick={handleToggle}
        disabled={isPending}
        aria-label={language === "th" ? "เปิด/ปิดสินค้า" : "Enable/disable item"}
        className="scale-[0.65] origin-center"
      />
    </div>
  );
}
